import React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { css } from '@emotion/react';
import { color } from '../../styles';
import { IGetDiaperRes } from '../../types/api/care-settings/getDiaper';
import { Cell, CellBase, HeaderCell, Row, Table, TableLinkText, TableLinkCell } from '../../styles/table';
import { Dialog } from '../mui/dialog/Dialog';
import { InputFormCell } from './inputFormCell';
import api from '../../../src/api';
import { useSnackbar } from '../../contexts/SnackbarContext';

type DiaperRow = IGetDiaperRes & {
  unitName?: string;
  bagsPerCase?: number;
  caseRequiredStock?: number | null;
  bagRequiredStock?: number | null;
  caseBorderLine?: number | null;
  bagBorderLine?: number | null;
};

interface Props {
  diapers: DiaperRow[];
  type: 'outer' | 'inner';
  url: string;
  dataFetch: () => void;
  onUpdate: (
    diaper: DiaperRow,
    values: { caseRequiredStock: number; bagRequiredStock: number; caseBorderLine: number; bagBorderLine: number }
  ) => Promise<void> | void;
}

const MinW120 = css`
  min-width: 120px;
`;

const ModalText = css`
  margin-bottom: 16px;
  font-size: 14px;
`;

const ModalSubText = css`
  color: ${color.red};
  font-size: 14px;
  font-weight: bold;
`;

const ModalFormContainer = css`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  align-items: center;
  justify-content: center;
`;

// テーブルのセルの横幅を左から順に定義
const CellWidth = [
  { flex: '1 0 76px' }, // 定額対象
  { flex: '1 1 120px' }, // シリーズ
  { flex: '3 1 250px' }, // 製品名
  { flex: '1 1 100px' }, // 単価
  { flex: '1 1 100px' }, // あるべき在庫（合計袋）
  { flex: '1 1 100px' }, // 発注ライン（合計袋）
  { flex: '0 0 188px' }, // 操作
];

export const DiaperTable = React.memo(({ diapers, type, url, dataFetch, onUpdate }: Props) => {
  const { nursingHomeId } = useParams<{ nursingHomeId: string }>();
  const { showSnackbar } = useSnackbar();
  const history = useHistory();
  const header = ['定額対象', 'シリーズ', '製品名', '単価', 'あるべき在庫', '発注ライン', '操作'];
  const [isModalOpen, setIsModalOpen] = React.useState<boolean>(false);
  const [selectedData, setSelectedData] = React.useState<IGetDiaperRes>();
  const [isRequiredStockModalOpen, setIsRequiredStockModalOpen] = React.useState<boolean>(false);
  const [selectedDiaper, setSelectedDiaper] = React.useState<DiaperRow | undefined>();
  const [formValues, setFormValues] = React.useState<{
    caseRequiredStock: number;
    bagRequiredStock: number;
    caseBorderLine: number;
    bagBorderLine: number;
  }>({ caseRequiredStock: 0, bagRequiredStock: 0, caseBorderLine: 0, bagBorderLine: 0 });

  const handleClickEdit = (id: number) => () => {
    history.push(`${url}/diaper-${type}/${id}/edit`);
  };

  const handleClickRequiredStockEdit = (data: DiaperRow) => () => {
    setSelectedDiaper(data);
    setFormValues({
      caseRequiredStock: data.caseRequiredStock ?? 0,
      bagRequiredStock: data.bagRequiredStock ?? 0,
      caseBorderLine: data.caseBorderLine ?? 0,
      bagBorderLine: data.bagBorderLine ?? 0,
    });
    setIsRequiredStockModalOpen(true);
  };

  const handleClickDelete = (data: IGetDiaperRes) => async () => {
    setSelectedData(data);
    setIsModalOpen(true);
  };

  const onModalClose = () => {
    setIsModalOpen(false);
    dataFetch();
  };

  const handleClickModalSubmit = async () => {
    if (selectedData) {
      await api.delete('/diapers/:nursingHomeDiaperId', {
        params: { nursingHomeDiaperId: selectedData.id },
      });

      setTimeout(() => {
        onModalClose();
        showSnackbar('削除しました');
      }, 500);
    }
  };

  const OperationCell = (data: DiaperRow) => {
    return (
      <div css={TableLinkCell}>
        <div css={TableLinkText} onClick={handleClickRequiredStockEdit(data)}>
          在庫管理
        </div>
        <div css={TableLinkText} onClick={handleClickEdit(data.id)}>
          編集
        </div>
        {!data.isFlatRate && (
          <div css={TableLinkText} onClick={handleClickDelete(data)}>
            削除
          </div>
        )}
      </div>
    );
  };

  const formatCaseBag = (cases?: number | null, bags?: number | null) => {
    if (cases == null && bags == null) return '-';
    const c = cases ?? 0;
    const b = bags ?? 0;
    if (c > 0 && b > 0) return `${c}ケース + ${b}袋`;
    if (c > 0) return `${c}ケース`;
    if (b > 0) return `${b}袋`;
    return `0袋`;
  };

  return (
    <>
      <div css={Table}>
        <div css={Row}>
          {header.map((hData, i) => {
            return (
              <div css={[CellBase, HeaderCell, CellWidth[i]]} key={i}>
                {hData}
              </div>
            );
          })}
        </div>
        {diapers.map((diaper, i) => {
          return (
            <div css={Row} key={i}>
              <div css={[CellBase, Cell, CellWidth[0]]}>{diaper.isFlatRate ? '対象' : '対象外'}</div>
              <div css={[CellBase, Cell, CellWidth[1]]}>{diaper.diaperSeries}</div>
              <div css={[CellBase, Cell, CellWidth[2]]}>{diaper.diaperName}</div>
              <div css={[CellBase, Cell, CellWidth[3]]}>{diaper.cost}円</div>
              <div css={[CellBase, Cell, CellWidth[4]]}>
                {formatCaseBag(diaper.caseRequiredStock, diaper.bagRequiredStock)}
              </div>
              <div css={[CellBase, Cell, CellWidth[5]]}>
                {formatCaseBag(diaper.caseBorderLine, diaper.bagBorderLine)}
              </div>
              <div css={[CellBase, Cell, CellWidth[6]]}>{OperationCell(diaper)}</div>
            </div>
          );
        })}
      </div>
      {isModalOpen && (
        <Dialog isOpen={isModalOpen} title={'項目の削除'} onClose={onModalClose} onOkClick={handleClickModalSubmit}>
          <div css={ModalText}>下記のデータを削除しますか?</div>
          <div css={ModalSubText}>
            {selectedData?.diaperShape === 'inner' ? 'パッド' : 'テープ/パンツ'} / {selectedData?.diaperSeries}
          </div>
          <div css={ModalSubText}>{selectedData?.diaperName}</div>
        </Dialog>
      )}
      {isRequiredStockModalOpen && (
        <Dialog
          isOpen={isRequiredStockModalOpen}
          title={'あるべき在庫・発注ラインの変更'}
          okText={'更新'}
          onClose={() => setIsRequiredStockModalOpen(false)}
          onOkClick={async () => {
            if (selectedDiaper) {
              await onUpdate(selectedDiaper, formValues);
            }
            setIsRequiredStockModalOpen(false);
          }}
        >
          <div css={ModalText}>対象ユニット: {selectedDiaper?.unitName}</div>
          <div css={ModalText}>
            {selectedDiaper?.diaperSeries}
            <br />
            {selectedDiaper?.diaperName}
          </div>
          <div css={ModalFormContainer}>
            <div css={[ModalSubText, MinW120]}>あるべき在庫: </div>
            <InputFormCell
              value={formValues.caseRequiredStock}
              id='modal-required-case'
              label='ケース'
              min={0}
              max={9999}
              onChange={(v) => setFormValues((s) => ({ ...s, caseRequiredStock: Number(v || 0) }))}
            />
            <InputFormCell
              value={formValues.bagRequiredStock}
              id='modal-required-bag'
              label='袋'
              min={0}
              max={9999}
              onChange={(v) => setFormValues((s) => ({ ...s, bagRequiredStock: Number(v || 0) }))}
            />
            <div css={MinW120}>
              合計袋数:
              {(formValues.caseRequiredStock ?? 0) * (selectedDiaper?.bagsPerCase ?? 0) +
                (formValues.bagRequiredStock ?? 0)}
              袋
            </div>
          </div>
          <div css={ModalFormContainer}>
            <div css={[ModalSubText, MinW120]}>発注ライン: </div>
            <InputFormCell
              value={formValues.caseBorderLine}
              id='modal-borderline-case'
              label='ケース'
              min={0}
              max={9999}
              onChange={(v) => setFormValues((s) => ({ ...s, caseBorderLine: Number(v || 0) }))}
            />
            <InputFormCell
              value={formValues.bagBorderLine}
              id='modal-borderline-bag'
              label='袋'
              min={0}
              max={9999}
              onChange={(v) => setFormValues((s) => ({ ...s, bagBorderLine: Number(v || 0) }))}
            />
            <div css={MinW120}>
              合計袋数:
              {(formValues.caseBorderLine ?? 0) * (selectedDiaper?.bagsPerCase ?? 0) + (formValues.bagBorderLine ?? 0)}
              袋
            </div>
          </div>
        </Dialog>
      )}
    </>
  );
});
